/**
 * Helper file providing methods to add the draggable and/or droppable
 * capability to a tag brush.
 */

const nextId = (() => {
	let id = 0;

	return () => `draggable-item-id-${id++}`;
})();

const indexAttributeKey = "data-original-index";

/**
 * Stores the bindings between a tagBrush id, and the model
 * corresponding to the droppable tagBrush. This allows to trigger
 * events with domain objects instead of anonymous tag brushes.
 *
 * The binding are scoped by "container" (usually the widget rendering the tag
 * brushes) to allow the GC to free the bindings when the container is gone.
 *
 * @type {WeakMap<Widget, Map<string, object>>}
 */
const idToModelPerContainer = new WeakMap();

function storeItem({ container, item, id }) {
	let map = idToModelPerContainer.get(container);
	if (!map) {
		map = {};
		idToModelPerContainer.set(container, map);
	}

	map[id] = item;
}

function getStoredItem({ container, id }) {
	const map = idToModelPerContainer.get(container);
	return map?.[id];
}

function ensureId(tagBrush, { shouldBeLowerCase = false } = {}) {
	const existingId = tagBrush?.element?.id;
	if (existingId) {
		if (shouldBeLowerCase && existingId !== existingId.toLowerCase()) {
			throw new Error(
				`This tag brush id mush be lowercase, but found "${existingId}"`,
			);
		}
		return existingId;
	}

	const id = nextId();
	tagBrush.attr({ id });
	return id;
}

function dragStart(event, { content } = {}) {
	const currentTarget = content ?? event.currentTarget;

	// We add, and "just" after that remove the "ghost-target" class
	// so that we can style the UI element just for the drag snapshot.
	// This way, we can control with CSS how the dragged item looks like
	// without having to create an image by hand.
	setTimeout(() => {
		currentTarget.classList.remove("ghost-target");
		currentTarget.classList.add("drag-target");
	}, 0);

	currentTarget.classList.add("ghost-target");

	if (content) {
		const rect = content.getBoundingClientRect();

		event.dataTransfer.setDragImage(
			content,
			-rect.x + event.clientX,
			-rect.y + event.clientY,
		);
	}
	event.dataTransfer.dropEffect = "move";

	const siblings = [...currentTarget.parentNode.children];

	currentTarget.attributes[indexAttributeKey] =
		siblings.indexOf(currentTarget);

	// We need to work around a "security" feature of webkit allowing to access `getData`
	// only in drop events.
	// Thus, we use the `setData` key to store our object.
	event.dataTransfer.setData(
		currentTarget.id, // this only works because the id is fully lowercase
		"",
	);
}

function dragOver(event) {
	event.preventDefault();
	const node = event.currentTarget;
	const draggedNodeId = event.originalEvent.dataTransfer.types[0];

	if (node.id === draggedNodeId) {
		return;
	}

	const draggedNode = document.getElementById(draggedNodeId);

	if (!draggedNode) {
		return;
	}

	const siblings = [...node.parentNode.children];

	const nodeIndex = siblings.indexOf(node);
	const draggedNodeIndex = siblings.indexOf(draggedNode);

	const position = nodeIndex < draggedNodeIndex ? "beforebegin" : "afterend";

	node.insertAdjacentElement(position, draggedNode);
}

function rollbackDrag({ event, siblings }) {
	const index = +event.currentTarget.attributes[indexAttributeKey];
	const draggedNode = event.currentTarget;

	const node = siblings[index];
	const draggedNodeIndex = siblings.indexOf(draggedNode);

	const position = index < draggedNodeIndex ? "beforebegin" : "afterend";

	node.insertAdjacentElement(position, draggedNode);
}

function dragEnd({ container, onChange }) {
	return (event) => {
		const dataTransfer = event.originalEvent.dataTransfer;
		const siblings = [...event.currentTarget.parentNode.children];

		const isDragValid =
			dataTransfer.__hasValidDropEffect__ ??
			dataTransfer.dropEffect !== "none";

		const found = findClosestNodeWithClass({
			class: "drag-target",
			node: event.currentTarget,
		});

		found?.classList.remove("drag-target");

		if (isDragValid) {
			onChange({
				items: siblings
					.map((node) => getStoredItem({ container, id: node.id }))
					.filter(Boolean),
			});
		} else {
			rollbackDrag({ event, siblings });
		}

		event.preventDefault();
	};
}

/**
 * Wraps a tag brush and make it draggable.
 *
 * @param {TagBrush} tagBrush - Html canvas tag brush to wrap
 * @param {object} [options] - Options to tweak the drag
 * @param {boolean} [options.when] - When `true`, make the tag brush draggable
 * @return {TagBrush}
 */
export function makeDraggable(tagBrush, { when = true, getDragHandle } = {}) {
	if (!when) {
		return tagBrush;
	}

	tagBrush.addClass("draggable");
	ensureId(tagBrush);

	let anchor = tagBrush.element;
	let options = {};

	if (getDragHandle) {
		anchor = getDragHandle(tagBrush.element);
		options = { content: tagBrush.element };
	}

	anchor.setAttribute("draggable", "true");

	anchor.addEventListener("dragstart", (event) => {
		dragStart(event, options);
	});

	return tagBrush;
}

/**
 * Wraps a tag brush and make it droppable.
 *
 * @param {TagBrush} tagBrush - Html canvas tag brush to wrap
 * @param {object} [options] - Options to tweak the drop
 * @param {boolean} [options.when] - When `true`, make the tag brush droppable
 * @param {function} [options.onChange] - Callback invoked with the new sorted
 *   items at the end of the drop action
 * @param {object} [options.item] - Model corresponding to the dropped UI
 *   element
 * @param {Widget} [options.container] - Widget rendering the tag brush.
 *   This is used to keep weak references to the droppable elements
 * @return {TagBrush}
 */
export function makeDroppable(
	tagBrush,
	{ when = true, onChange = () => {}, item, container } = {},
) {
	if (!when) {
		return tagBrush;
	}

	const id = ensureId(tagBrush, { shouldBeLowerCase: true });

	if (item && container) {
		storeItem({ container, item, id });
	}

	tagBrush.addClass("droppable");

	tagBrush.attr({
		dragover: dragOver,
		dragend: dragEnd({ container, onChange }),
	});

	return tagBrush;
}

/**
 * Register siblings DOM elements (via their tagBrush) of droppable nodes, so
 * that the `onChange` callback of the method `makeDroppable` can return all
 * the items, not only the droppable ones.
 *
 * @param {TagBrush} tagBrush - Html canvas tag brush to wrap
 * @param {object} [item] - Model corresponding to the dropped UI
 *   element
 * @param {Widget} [container] - Widget rendering the tag brush.
 *   This is used to keep weak references to the droppable elements
 * @return {TagBrush}
 */
export function registerNonDroppable({ tagBrush, item, container = {} }) {
	if (!item || !container) {
		return tagBrush;
	}

	const id = ensureId(tagBrush, { shouldBeLowerCase: true });
	storeItem({ container, item, id });

	return tagBrush;
}

function findClosestNodeWithClass({ class: className, node }) {
	if (!node) return null;

	if (node.classList.contains(className)) {
		return node;
	}

	return findClosestNodeWithClass({
		class: className,
		node: node.parentElement,
	});
}
